<template>
  <div class="grid grid-cols-1 gap-y-7 mb-7">
    <div
      v-if="
        isGetMembershipButtonSkeletonVisible ||
        isGetChannelMembershipButtonVisible
      "
      class="mx-5"
    >
      <GetChannelMembershipButtonSkeleton
        v-if="isGetMembershipButtonSkeletonVisible"
      />
      <GetChannelMembershipButton
        v-else-if="isGetChannelMembershipButtonVisible"
      />
    </div>

    <ChannelRecentActivityRow
      v-if="isSectionVisible(ChannelSectionName.CommunityFeed)"
      class="px-5 h-fit"
    />

    <ChannelMvpLeaderboardSection
      v-if="isSectionVisible(ChannelSectionName.Leaderboard)"
      :new-layout="false"
    />

    <ContestsSection
      v-if="isSectionVisible(ChannelSectionName.Contests)"
      id="contests"
      :style="
        canUseTokenGating ? { 'grid-row': channelPositionI + 1 } : undefined
      "
      :contests="contests"
      :loading="loadingContests"
      :loading-more="loadingMoreContests"
      :has-more="hasMoreContests"
      @refetch="refetchContests"
      @load-more="loadMoreContests"
    />

    <ShowSection
      v-if="isSectionVisible(ChannelSectionName.Shows)"
      id="shows"
      data-testid="channel-section-shows"
      :style="canUseTokenGating ? { 'grid-row': channelPositionI } : undefined"
      :shows="shows"
      :loading="loadingShows"
      :loading-more="loadingMoreShows"
      :has-more="hasMoreShows"
      @load-more="loadMoreShows"
      @refetch="refetchShows"
    >
      <template #actions>
        <RouterLink
          :to="{
            name: RouteNames.ShowList,
            params: { channel: channelStore.channelSlug },
          }"
          custom
        >
          <template #default="{ navigate }">
            <ButtonV2 variant="subtle" size="md" class="px-6" @click="navigate">
              {{ $t("channel.buttons.browseAllShows") }}
            </ButtonV2>
          </template>
        </RouterLink>
      </template>
    </ShowSection>

    <OffersSection
      v-if="isSectionVisible(ChannelSectionName.Offers) && enableCouponOffers"
      :offers="offers"
      :loading="loadingOffers"
      :loading-more="loadingMoreOffers"
      :has-more="hasMoreOffers"
      @load-more="loadMoreOffers"
      @refetch="refetchOffers"
    />

    <ThreadsSection
      v-if="isSectionVisible(ChannelSectionName.Threads)"
      id="threads"
      class="px-5"
    />
  </div>
</template>

<script setup lang="ts">
/**
 * Legacy channel page body (feature flag OFF). Byte-for-byte unchanged from the
 * pre-redesign layout: the `channelPositionI` / `grid-row` token-gating layout
 * hack lives ONLY here.
 *
 * Section order: CommunityFeed (MEANWHILE) -> Leaderboard -> Contests (QUESTS)
 * -> Shows -> Offers (REWARDS) -> Threads.
 *
 * All data / handlers come from `useChannelPage()` (run in channel.vue's setup)
 * via inject; this component is presentation-only.
 */
import { inject } from "vue";
import { ChannelSectionName } from "@/generated/graphql";
import { RouteNames } from "@/router/app.router";
import { ChannelPageKey } from "@/composables/channel/use-channel-page";

import ButtonV2 from "../ui/button/button-v2.vue";
import ChannelRecentActivityRow from "./channel-recent-activity-row.vue";
import ChannelMvpLeaderboardSection from "../leaderboard/channel-mvp-leaderboard-section.vue";
import ShowSection from "./shows/show-section.vue";
import ThreadsSection from "@/components/channel/threads/threads-section.vue";
import ContestsSection from "@/components/channel/contests/contest-section.vue";
import OffersSection from "@/components/channel/offers/offers-section.vue";
import GetChannelMembershipButton from "./channel-page/get-channel-membership-button.vue";
import GetChannelMembershipButtonSkeleton from "@/components/ui/skeleton/get-channel-membership-button-skeleton.vue";

const {
  isSectionVisible,
  canUseTokenGating,
  channelPositionI,
  enableCouponOffers,
  channelStore,
  contests,
  loadingContests,
  loadingMoreContests,
  hasMoreContests,
  refetchContests,
  loadMoreContests,
  shows,
  loadingShows,
  loadingMoreShows,
  hasMoreShows,
  loadMoreShows,
  refetchShows,
  offers,
  loadingOffers,
  loadingMoreOffers,
  hasMoreOffers,
  loadMoreOffers,
  refetchOffers,
  isGetMembershipButtonSkeletonVisible,
  isGetChannelMembershipButtonVisible,
} = inject(ChannelPageKey)!;
</script>
